import { Context } from "%/dynamic-component-editor/classes/context-v2";
import ProvidesContextsResolver from "%/dynamic-component-editor/classes/provides-contexts-resolver";
import {
  buildComponentContexts,
  unexpectedContextProblems,
  malformedRequiredProblems,
} from "@bloglovin/inspire-engine/DynamicComponents/Helpers/build-component-contexts.js";
import { ContextValidationError } from "@bloglovin/inspire-engine/Exceptions/ContextValidationError.js";
import {
  createEngineContextBridge,
  getDbAppsCatalog,
  describeDynamicComponent,
  readProvidesContexts,
  readRequiredContexts,
} from "@studio/Helpers/studio-installed-apps-bootstrap";
import { getHostEntityTypes } from "@studio/Helpers/studio-legacy-entities-defs-bootstrap";
import { renderSurfaceKind, isCompiledSurfaceKind }
  from "%/dynamic-component-editor/helpers/context-error-report";
import { isCompiledRenderSurface, contextualAppName, resolveContextEngineAppName }
  from "%/dynamic-component-editor/classes/platform-standard-entities";
import { resolveChainedContexts } from "%/dynamic-component-editor/helpers/resolve-chained-contexts";
import { dropUnpublishedStoryContexts }
  from "%/dynamic-component-editor/helpers/drop-unpublished-story-contexts";
import App from "@bloglovin/inspire-engine/App";
import * as PrimitiveContextValue from "@bloglovin/inspire-engine/Helpers/PrimitiveContextValue";

export default {
  inject: {
    inheritedScopedProvidedContexts: {
      from: "scopedProvidedContexts",
      default: null,
    },
  },
  provide() {
    return {
      scopedProvidedContexts: () => {
        const available = this.publishedOnlyScopedContexts(this.fullAvailableScopedContexts());
        const defs = this.scopedContextComponentDefs();
        return defs ? buildComponentContexts(available, defs.required, defs.provides) : available;
      },
    };
  },
  data() {
    return {
      scopedContexts: {},
      scopedResolveStartedForKey: null,
    };
  },
  computed: {
    scopedContextProblems() {
      const defs = this.scopedContextComponentDefs();
      if (!defs) {
        return [];
      }
      const output = buildComponentContexts(this.fullAvailableScopedContexts(), defs.required, defs.provides);
      return [
        ...unexpectedContextProblems(this.extraScopedEntityContexts(), defs.required, defs.provides),
        ...malformedRequiredProblems(output, defs.required),
      ];
    },
    scopedContextIsBroken() {
      return this.scopedContextProblems.length > 0 && !isCompiledSurfaceKind(renderSurfaceKind());
    },
    scopedContextGatePasses() {
      const problems = this.scopedContextProblems;
      if (problems.length && isCompiledSurfaceKind(renderSurfaceKind())) {
        // XXX: intentional hard fail — do NOT catch or soften. A leaked/malformed
        // XXX: context payload is a build-site bug: staging must break the page
        // XXX: loudly, the builder renders the element in a broken state so it can
        // XXX: be healed. See build-component-contexts.js / ContextValidationError.
        throw new ContextValidationError(this.scopedContextComponentDefs()?.componentName ?? "component", problems);
      }
      return problems.length === 0;
    },
  },
  methods: {
    scopedContextComponentDefs() {
      return null;
    },
    publishedOnlyScopedContexts(contexts) {
      return dropUnpublishedStoryContexts(contexts, {
        onRenderSurface: isCompiledRenderSurface(),
        isPublishedFor: storyId => {
          try {
            return App.get(contextualAppName())
                .getEntityField("Story", { id: String(storyId) }, "isPublished");
          } catch {
            return undefined;
          }
        },
      });
    },
    fullAvailableScopedContexts() {
      return resolveChainedContexts(
        {
          ...this.inheritedScopedProvidedContexts?.() ?? {},
          ...this.scopedContexts,
          ...this.extraScopedEntityContexts(),
          ...this.extraScopedPrimitiveContexts(),
        },
        name => Context.allContexts[name] ?? null,
        (entityType, pk, field, appName) => App.get(resolveContextEngineAppName(appName)).getEntityField(entityType, pk, field),
      );
    },
    lookupExtraScopedContext() {
      return null;
    },
    extraScopedPrimitiveContexts() {
      return {};
    },
    extraScopedEntityContexts() {
      return {};
    },
    resolveScopedProvidedContexts(dynamicComponent, instanceKey) {
      if (!dynamicComponent || this.scopedResolveStartedForKey === instanceKey) {
        return;
      }
      if (!window.blclient?.data?.inspirationUserId) {
        return;
      }
      const requiredContexts = readRequiredContexts(dynamicComponent);
      const providesContexts = {};
      Object.entries(readProvidesContexts(dynamicComponent)).forEach(([contextName, entry]) => {
        if (entry?.entitySource === "uri" && requiredContexts[contextName]) {
          return;
        }
        providesContexts[contextName] = entry;
      });
      if (!Object.keys(providesContexts).length) {
        return;
      }
      this.scopedResolveStartedForKey = instanceKey;
      ProvidesContextsResolver.resolveAll({
        providesContexts,
        urlVars: null,
        catalog: getDbAppsCatalog(),
        hostEntityTypes: getHostEntityTypes(),
        allowUri: false,
        declaredContextNames: Object.keys(requiredContexts),
        lookupContext: name => this.lookupExtraScopedContext(name)
          ?? this.inheritedScopedProvidedContexts?.()[name]
          ?? Context.getContext(name)
          ?? null,
        registerContext: (name, context) => {
          this.scopedContexts = { ...this.scopedContexts, [name]: context };
        },
        primitiveContextValue: PrimitiveContextValue,
        registerValueContext: (name, value) => {
          this.scopedContexts = { ...this.scopedContexts, [name]: value };
        },
        bridge: createEngineContextBridge(),
        measureLabel: "component",
        surfaceLabel: describeDynamicComponent(dynamicComponent),
      });
    },
  },
};
